/** Dedicated billing page: credit balance + 4 packs grid + Polar checkout */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, AlertCircle, Info, FlaskConical } from 'lucide-react'
import { useAppStore } from '../stores/app-store'
import { cn } from '../lib/utils'
import {
  useCreditBalance,
  useCreditPacks,
  useCreditPurchases,
  useCreditTransactions,
  useCreateCheckout,
  usePaymentsConfig,
} from '../hooks/use-credits'
import { CreditPackCard } from '../components/billing/credit-pack-card'
import { CreditBalanceCard } from '../components/billing/credit-balance-card'
import { BillingLedgerPanel } from '../components/billing/billing-ledger-panel'

const POPULAR_PACK_ID = 'pack_starter'
const LEDGER_PAGE_SIZE = 5

export function BillingPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { theme } = useAppStore()
  const isDark = theme === 'dark'
  const [transactionType, setTransactionType] = useState('all')
  const [transactionOffset, setTransactionOffset] = useState(0)
  const [purchaseOffset, setPurchaseOffset] = useState(0)

  const { data: balance, isLoading: balanceLoading, refetch: refetchBalance } = useCreditBalance()
  const { data: packs, isLoading: packsLoading, error: packsError } = useCreditPacks()
  const { data: paymentsConfig } = usePaymentsConfig()
  const { data: transactions, isLoading: transactionsLoading } = useCreditTransactions({
    type: transactionType === 'all' ? undefined : transactionType,
    limit: LEDGER_PAGE_SIZE,
    offset: transactionOffset,
  })
  const { data: purchases, isLoading: purchasesLoading } = useCreditPurchases({
    limit: LEDGER_PAGE_SIZE,
    offset: purchaseOffset,
  })
  const checkout = useCreateCheckout()

  const status = searchParams.get('status')
  // Polar appends `customer_session_token` to its redirect; in some flows it
  // replaces our `?status=success` query entirely. Treat its presence as a
  // success signal so the balance refetches even when `status` is missing.
  const hasPolarSessionToken = searchParams.has('customer_session_token')
  const isSuccess = status === 'success' || hasPolarSessionToken
  const isSandbox = paymentsConfig?.environment === 'sandbox'

  // After Polar redirect, refresh balance (success only) and clear params after 5s
  useEffect(() => {
    if (!isSuccess && status !== 'cancel') return

    if (isSuccess) refetchBalance()

    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams)
      next.delete('status')
      next.delete('customer_session_token')
      setSearchParams(next, { replace: true })
    }, 5000)
    return () => clearTimeout(timer)
  }, [isSuccess, status, refetchBalance, searchParams, setSearchParams])

  const handleBuy = async (packId: string) => {
    try {
      const result = await checkout.mutateAsync({ packId })
      if (!result?.checkoutUrl) {
        console.error('Checkout returned no URL', result)
        return
      }
      window.location.href = result.checkoutUrl
    } catch (err) {
      console.error('Checkout failed:', err)
    }
  }

  const handleTransactionTypeChange = (type: string) => {
    setTransactionType(type)
    setTransactionOffset(0)
  }

  const checkoutErrorMsg = checkout.error?.message
  const isCheckoutProviderUnconfigured = checkout.error?.status === 503
  const isPacksProviderUnconfigured = (packsError as { status?: number } | null)?.status === 503
  const isProviderUnconfigured = isCheckoutProviderUnconfigured || isPacksProviderUnconfigured
  const showGenericPacksError = packsError && !isPacksProviderUnconfigured

  return (
    <div className={cn('min-h-dvh', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
      {/* Header */}
      <div
        className={cn(
          'border-b px-4 py-4 md:px-6',
          isDark ? 'border-white/[0.06]' : 'border-neutral-200',
        )}
      >
        <div className="mx-auto flex max-w-4xl items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className={cn(
              'cursor-pointer rounded-lg p-2 md:p-1.5',
              isDark
                ? 'hover:bg-surface-3 active:bg-surface-3 text-neutral-400'
                : 'hover:bg-neutral-100 active:bg-neutral-100 text-neutral-500',
            )}
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5 md:h-4 md:w-4" />
          </button>
          <h1
            className={cn(
              'text-lg font-semibold',
              isDark ? 'text-neutral-100' : 'text-neutral-900',
            )}
          >
            Billing
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-8 space-y-6">
        {/* Sandbox-mode warning — fail-safe: shown unless explicitly production */}
        {isSandbox && (
          <div
            className={cn(
              'flex items-start gap-3 rounded-lg border p-4',
              isDark ? 'border-violet-500/30 bg-violet-500/10' : 'border-violet-300 bg-violet-50',
            )}
          >
            <FlaskConical className="h-5 w-5 shrink-0 text-violet-500" />
            <div>
              <p
                className={cn(
                  'text-sm font-medium',
                  isDark ? 'text-violet-300' : 'text-violet-800',
                )}
              >
                Sandbox mode
              </p>
              <p
                className={cn('text-xs mt-0.5', isDark ? 'text-violet-400/80' : 'text-violet-700')}
              >
                Checkouts use Polar sandbox — no real charges. Use a sandbox test card.
              </p>
            </div>
          </div>
        )}

        {/* Status banners */}
        {isSuccess && (
          <div
            className={cn(
              'flex items-start gap-3 rounded-lg border p-4',
              isDark
                ? 'border-emerald-500/30 bg-emerald-500/10'
                : 'border-emerald-300 bg-emerald-50',
            )}
          >
            <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" />
            <div>
              <p
                className={cn(
                  'text-sm font-medium',
                  isDark ? 'text-emerald-300' : 'text-emerald-800',
                )}
              >
                Payment successful
              </p>
              <p
                className={cn(
                  'text-xs mt-0.5',
                  isDark ? 'text-emerald-400/80' : 'text-emerald-700',
                )}
              >
                Your credits will appear in your balance shortly. We&apos;ll refresh automatically.
              </p>
            </div>
          </div>
        )}

        {status === 'cancel' && (
          <div
            className={cn(
              'flex items-start gap-3 rounded-lg border p-4',
              isDark
                ? 'border-neutral-500/30 bg-neutral-500/10'
                : 'border-neutral-300 bg-neutral-50',
            )}
          >
            <Info
              className={cn('h-5 w-5 shrink-0', isDark ? 'text-neutral-400' : 'text-neutral-500')}
            />
            <div>
              <p
                className={cn(
                  'text-sm font-medium',
                  isDark ? 'text-neutral-200' : 'text-neutral-800',
                )}
              >
                Checkout cancelled
              </p>
              <p className={cn('text-xs mt-0.5', isDark ? 'text-neutral-400' : 'text-neutral-600')}>
                No charge was made. Pick a pack below to try again.
              </p>
            </div>
          </div>
        )}

        {isProviderUnconfigured && (
          <div
            className={cn(
              'flex items-start gap-3 rounded-lg border p-4',
              isDark ? 'border-amber-500/30 bg-amber-500/10' : 'border-amber-300 bg-amber-50',
            )}
          >
            <AlertCircle className="h-5 w-5 shrink-0 text-amber-500" />
            <div>
              <p
                className={cn('text-sm font-medium', isDark ? 'text-amber-300' : 'text-amber-800')}
              >
                Payment system not configured
              </p>
              <p className={cn('text-xs mt-0.5', isDark ? 'text-amber-400/80' : 'text-amber-700')}>
                The payment provider is unavailable. Please contact support.
              </p>
            </div>
          </div>
        )}

        {checkoutErrorMsg && !isCheckoutProviderUnconfigured && (
          <div
            className={cn(
              'flex items-start gap-3 rounded-lg border p-4',
              isDark ? 'border-red-500/30 bg-red-500/10' : 'border-red-300 bg-red-50',
            )}
          >
            <AlertCircle className="h-5 w-5 shrink-0 text-red-500" />
            <p className={cn('text-sm', isDark ? 'text-red-300' : 'text-red-800')}>
              {checkoutErrorMsg}
            </p>
          </div>
        )}

        {/* Balance */}
        <CreditBalanceCard balance={balance} isLoading={balanceLoading} isDark={isDark} />

        <BillingLedgerPanel
          balance={balance}
          paymentsConfig={paymentsConfig}
          transactions={transactions}
          purchases={purchases}
          transactionType={transactionType}
          onTransactionTypeChange={handleTransactionTypeChange}
          onTransactionPageChange={setTransactionOffset}
          onPurchasePageChange={setPurchaseOffset}
          transactionsLoading={transactionsLoading}
          purchasesLoading={purchasesLoading}
          isDark={isDark}
        />

        {/* Packs */}
        <div>
          <h2
            className={cn(
              'text-sm font-semibold mb-3',
              isDark ? 'text-neutral-200' : 'text-neutral-800',
            )}
          >
            Buy credits
          </h2>

          {showGenericPacksError && (
            <p className={cn('text-sm', isDark ? 'text-red-400' : 'text-red-600')}>
              Failed to load credit packs. Please refresh the page.
            </p>
          )}

          {packsLoading && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={cn(
                    'h-56 animate-pulse rounded-xl border',
                    isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
                  )}
                />
              ))}
            </div>
          )}

          {packs && packs.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {packs.map((pack) => (
                <CreditPackCard
                  key={pack.id}
                  pack={pack}
                  isPopular={pack.id === POPULAR_PACK_ID}
                  isLoading={checkout.isPending}
                  isDark={isDark}
                  onBuy={handleBuy}
                />
              ))}
            </div>
          )}

          {packs && packs.length === 0 && (
            <p className={cn('text-sm', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
              No credit packs are currently available.
            </p>
          )}
        </div>

        <p className={cn('text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
          Payments are processed securely by Polar. Credits are non-refundable and do not expire.
        </p>
      </div>
    </div>
  )
}
